import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

import {
    DEFAULT_LANGUAGE,
    SUPPORTED_LANGUAGE_CODES,
    getLanguageMeta,
} from './languages';

// One translation file per language (public/locales/{lng}.json). All strings —
// common UI, header, footer, seo, and page content — live inside that single
// file, organized into top-level sections (common, header, footer, seo, ...).
i18n
    // Loads /locales/{lng}.json from the public folder.
    .use(HttpBackend)
    // Determines the active language. During Phase 0 the language comes from the
    // URL path (/zh/...) or a stored preference; Phase A wires it to the router.
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        fallbackLng: DEFAULT_LANGUAGE,
        supportedLngs: SUPPORTED_LANGUAGE_CODES,
        // Map region variants (e.g. zh-CN) down to the base language key (zh).
        load: 'languageOnly',
        nonExplicitSupportedLngs: true,

        // Single namespace -> single file per language.
        ns: ['translation'],
        defaultNS: 'translation',
        fallbackNS: 'translation',

        backend: {
            loadPath: '/locales/{{lng}}.json',
        },

        detection: {
            // First path segment decides the language: /zh/... -> "zh".
            // Anything not in supportedLngs falls through to the default.
            order: ['path', 'localStorage', 'htmlTag'],
            lookupFromPathIndex: 0,
            lookupLocalStorage: 'i18nextLng',
            // The language switcher writes localStorage explicitly; we don't let
            // the detector auto-cache so the URL stays the single source of truth.
            caches: [],
        },

        // Missing/empty translations fall back to the default language instead of
        // rendering blanks — this is what keeps not-yet-translated content readable.
        returnEmptyString: false,

        interpolation: {
            escapeValue: false, // React already escapes output.
        },

        react: {
            useSuspense: true,
        },
    });

// Keep <html lang>/dir in sync with the active language — on initial load
// (from URL/localStorage detection) and on every switch.
const applyHtmlLang = (lng) => {
    if (typeof document === 'undefined') return;
    const meta = getLanguageMeta((lng || DEFAULT_LANGUAGE).split('-')[0]);
    document.documentElement.lang = meta.htmlLang;
    document.documentElement.dir = meta.dir || 'ltr';
};
i18n.on('languageChanged', applyHtmlLang);
i18n.on('initialized', () => applyHtmlLang(i18n.resolvedLanguage || i18n.language));

export default i18n;
